import { useCallback, useEffect, useRef, useState } from "react";
import type { OperationId } from "../api/operations";
import type { EventResource } from "../api/types";
import { useInvalidation, useServices } from "./services";

export type Resource<T> =
  | { status: "not_connected"; operation: OperationId }
  | { status: "loading" }
  | { status: "error"; error: unknown }
  | { status: "ready"; data: T; refreshing: boolean; refreshError: unknown | null };

/**
 * 画面のデータを取る。
 * - 契約に無い操作は呼ばずに not_connected を返す（画面は「未接続」と出す）
 * - SSE の「変わった」通知（invalidateOn）が来たら取り直す。取り直しの結果で丸ごと置きかえる（削除・非表示を残さない）
 */
export function useResource<T>(
  op: OperationId,
  load: () => Promise<T>,
  deps: unknown[],
  invalidateOn: EventResource[] = [],
): Resource<T> & { reload: () => void } {
  const { api } = useServices();
  const connected = api.isConnected(op);
  const tick = useInvalidation(invalidateOn);
  const [state, setState] = useState<Resource<T>>(() => (connected ? { status: "loading" } : { status: "not_connected", operation: op }));
  const [manual, setManual] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const seq = useRef(0);
  const depsKey = JSON.stringify(deps);
  const lastDeps = useRef(depsKey);

  useEffect(() => {
    if (!connected) {
      setState({ status: "not_connected", operation: op });
      return;
    }
    const mine = ++seq.current;
    const depsChanged = lastDeps.current !== depsKey;
    lastDeps.current = depsKey;
    setState((s) => (s.status === "ready" && !depsChanged ? { ...s, refreshing: true } : { status: "loading" }));
    loadRef
      .current()
      .then((data) => {
        if (mine === seq.current) setState({ status: "ready", data, refreshing: false, refreshError: null });
      })
      .catch((error) => {
        if (mine !== seq.current) return;
        setState((s) =>
          s.status === "ready" && !depsChanged ? { ...s, refreshing: false, refreshError: error } : { status: "error", error },
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, op, depsKey, tick, manual]);

  const reload = useCallback(() => setManual((n) => n + 1), []);
  return { ...state, reload };
}
